/**
 * useBookinglayer Hooks
 *
 * React Query hooks for all Bookinglayer integration operations.
 */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BookinglayerService, type SyncScopeRange } from '../services/bookinglayerService';
import type {
  BlCatalogDecision,
  ConnectPayload,
  BookinglayerSyncConfig,
  OutboundEventStatus,
  OutboundEventResourceType,
  OutboundEventsResponse,
} from '../types';

const KEYS = {
  status: ['bookinglayer', 'status'] as const,
  syncJobs: ['bookinglayer', 'syncJobs'] as const,
  syncConfig: ['bookinglayer', 'syncConfig'] as const,
  paymentGateways: ['bookinglayer', 'paymentGateways'] as const,
  catalog: ['bookinglayer', 'catalog'] as const,
  alerts: ['bookinglayer', 'alerts'] as const,
  seasonInfo: (locationId: string | null | undefined) =>
    ['bookinglayer', 'seasonInfo', locationId] as const,
  webhookLogs: (params: { outcome?: string; includeBody?: boolean; limit?: number } = {}) =>
    ['bookinglayer', 'webhookLogs', params] as const,
  webhookLog: (logId: string | null) =>
    ['bookinglayer', 'webhookLog', logId] as const,
};

/** Fetch connection status + sync config */
export function useConnectionStatus() {
  return useQuery({
    queryKey: KEYS.status,
    queryFn: () => BookinglayerService.getStatus(),
    retry: 1,
  });
}

/**
 * Fetch the connection's BL payment gateways (for the outbound gateway-mapping
 * editor). `enabled` gates the live BL call so it only fires when the editor is
 * actually shown (outbound payments on).
 */
export function useBlPaymentGateways(enabled = true) {
  return useQuery({
    queryKey: KEYS.paymentGateways,
    queryFn: () => BookinglayerService.getPaymentGateways(),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Fetch the BL catalog — every product the location sells, with its price
 * evidence and current mapping. `enabled` gates it so the live BL call only
 * fires when the Catalog tab is actually open; the response is stable enough
 * to cache for a few minutes (the catalog changes when BL's does, not per view).
 */
export function useBlCatalog(enabled = true) {
  return useQuery({
    queryKey: KEYS.catalog,
    queryFn: () => BookinglayerService.getCatalog(),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Fetch sync jobs history.
 *
 * Polling is the safety net: while any job is running, refetch every
 * 3s so the progress bar advances even if the WebSocket is down. The
 * primary update path is the realtime invalidation in
 * `useRealtimeInvalidation`, which fires on every backend job.save()
 * and triggers an immediate refetch — so the bar usually moves well
 * before the polling tick.
 */
export function useSyncJobs(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: KEYS.syncJobs,
    queryFn: () => BookinglayerService.getSyncJobs(),
    enabled: options?.enabled ?? true,
    refetchInterval: (query) => {
      const jobs = query.state.data;
      const hasRunning = jobs?.some((j) => j.status === 'running');
      return hasRunning ? 3_000 : false;
    },
    // Backend suppresses per-save realtime during the bulk sync, so this
    // poll is the ONLY progress feed — keep it ticking when the window is
    // hidden or the panel freezes mid-run for backgrounded tabs.
    refetchIntervalInBackground: true,
  });
}

/**
 * Latest running (or just-completed) sync job, for the global banner
 * + inline progress bar.
 *
 * Returns `null` when no job is running and no recent terminal job
 * exists. Otherwise returns the freshest job — running takes priority,
 * but a job that finished within the last 60s is also surfaced so the
 * UI can render the "Sync complete" tail before fading.
 */
export function useActiveSyncJob(options?: { enabled?: boolean }) {
  const { data: jobs, isLoading } = useSyncJobs(options);
  const job = (() => {
    if (!jobs?.length) return null;
    const running = jobs.find((j) => j.status === 'running');
    if (running) return running;
    const latest = jobs[0]; // sorted -started_at by the backend
    if (!latest?.completedAt) return null;
    const completedAt = new Date(latest.completedAt).getTime();
    if (Number.isNaN(completedAt)) return null;
    return Date.now() - completedAt < 60_000 ? latest : null;
  })();
  return { job, isLoading };
}

/**
 * Fetch sync config.
 *
 * `enabled` lets callers outside the integration page (e.g. the booking
 * payment flow checking whether outbound is on) skip the request for
 * non-integrated locations, where the endpoint has no connection to return.
 */
export function useSyncConfig(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: KEYS.syncConfig,
    queryFn: () => BookinglayerService.getSyncConfig(),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Fetch alerts. `enabled` lets the drawer defer the request until it is
 * actually opened — the dashboard badge reads `connection.alertLevel`
 * from the status endpoint, so nothing needs this list up-front.
 */
export function useAlerts(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: KEYS.alerts,
    queryFn: () => BookinglayerService.getAlerts(),
    enabled: options?.enabled ?? true,
  });
}

/** Validate API token */
export function useValidateToken() {
  return useMutation({
    mutationFn: (apiKey: string) => BookinglayerService.validateToken(apiKey),
  });
}

/** Preview bookings */
export function usePreviewBookings() {
  return useMutation({
    mutationFn: ({ apiKey, blLocationId }: { apiKey: string; blLocationId: number }) =>
      BookinglayerService.previewBookings(apiKey, blLocationId),
  });
}

/** Connect to Bookinglayer */
export function useConnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ConnectPayload) => BookinglayerService.connect(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.status });
      // connect() queues the initial sync and creates its running job. Refresh
      // the jobs list so <SyncProgressBanner>/<SyncProgressInline> pick it up
      // immediately — otherwise the running sync stays invisible (the poll only
      // self-sustains once a running job is already in the cached data), which
      // led operators to hit Sync again and start an overlapping run.
      qc.invalidateQueries({ queryKey: KEYS.syncJobs });
    },
  });
}

/**
 * Trigger manual sync — optional scope range maps to backend
 * starts_at / updated_at / created_at filters.
 */
export function useTriggerSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scope?: SyncScopeRange) => BookinglayerService.triggerSync(scope),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.syncJobs });
      qc.invalidateQueries({ queryKey: KEYS.status });
    },
  });
}

/**
 * Season info for the wizard's scope step. `enabled` defaults to
 * locationId being truthy so the hook is a no-op while the wizard hasn't
 * decided which Shaka location to bind to yet.
 */
export function useSeasonInfo(locationId: string | null | undefined) {
  return useQuery({
    queryKey: KEYS.seasonInfo(locationId ?? null),
    queryFn: () => BookinglayerService.getSeasonInfo(locationId as string),
    enabled: !!locationId,
    staleTime: 5 * 60_000,
  });
}

/**
 * Update sync config. Arming `outboundExecutionMode: 'live'` requires
 * `confirmLiveOutbound: true` alongside it — the backend rejects the
 * transition with 400 otherwise.
 */
export function useUpdateSyncConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (updates: Partial<BookinglayerSyncConfig> & { confirmLiveOutbound?: boolean }) =>
      BookinglayerService.updateSyncConfig(updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.syncConfig });
      qc.invalidateQueries({ queryKey: KEYS.status });
    },
  });
}

/**
 * Record what a BL product becomes here. Invalidates the catalog so the row
 * reflects the decision, and the service/activity lists because a link changes
 * their BL indicator.
 */
export function useSetCatalogDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      externalProductId, decision,
    }: { externalProductId: string; decision: BlCatalogDecision }) =>
      BookinglayerService.setCatalogDecision(externalProductId, decision),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.catalog });
      qc.invalidateQueries({ queryKey: ['services'] });
      qc.invalidateQueries({ queryKey: ['activities'] });
    },
  });
}

/**
 * Create the catalog rows the confirmed decisions call for. Idempotent, so a
 * double-click cannot duplicate the catalog.
 */
export function useImportCatalog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => BookinglayerService.importCatalog(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.catalog });
      qc.invalidateQueries({ queryKey: ['services'] });
      qc.invalidateQueries({ queryKey: ['activities'] });
    },
  });
}

/** Disconnect integration */
export function useDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => BookinglayerService.disconnect(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.status });
    },
  });
}

/** Recover from error state */
export function useRecover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => BookinglayerService.recover(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.status });
      qc.invalidateQueries({ queryKey: KEYS.syncJobs });
      qc.invalidateQueries({ queryKey: KEYS.alerts });
    },
  });
}

/** Permanently delete connection — removes webhooks from BL, API key, all config */
export function useDeleteConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => BookinglayerService.deleteConnection(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.status });
      qc.removeQueries({ queryKey: KEYS.syncJobs });
      qc.removeQueries({ queryKey: KEYS.syncConfig });
      qc.removeQueries({ queryKey: KEYS.alerts });
    },
  });
}

/** Retry webhook registration with BL */
export function useRegisterWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => BookinglayerService.registerWebhook(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.status });
      qc.invalidateQueries({ queryKey: ['bookinglayer', 'webhookLogs'] });
    },
  });
}

/**
 * Fetch inbound webhook logs (debug feed).
 * Use `includeBody=true` to also fetch headers + raw body + payload.
 */
export function useWebhookLogs(
  params: { outcome?: string; includeBody?: boolean; limit?: number } = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: KEYS.webhookLogs(params),
    queryFn: () => BookinglayerService.getWebhookLogs(params),
    enabled: options?.enabled ?? true,
  });
}

/**
 * One webhook log with its body. The body is guest PII, so this fetches
 * exactly the log the operator opened — never a prefix of the feed.
 */
export function useWebhookLog(logId: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: KEYS.webhookLog(logId),
    queryFn: () => BookinglayerService.getWebhookLog(logId!),
    enabled: (options?.enabled ?? true) && !!logId,
  });
}

/**
 * The audit log of every Shaka → BL push, paged for the drawer.
 *
 * Pages by `offset` rather than by a growing `limit`: the server caps `limit`
 * at 200 per request (OutboundEventsView.MAX_LIMIT), so a growing limit stops
 * advancing at 200 and history older than that becomes unreachable. `offset`
 * has no cap, and each page is fetched once instead of refetching the prefix.
 */
export function useOutboundEventsInfinite(
  params: {
    pageSize?: number;
    status?: OutboundEventStatus[];
    resourceType?: OutboundEventResourceType[];
  } = {},
  options?: { enabled?: boolean },
) {
  const pageSize = params.pageSize ?? 25;
  const q = useInfiniteQuery<OutboundEventsResponse>({
    queryKey: [
      'bookinglayer',
      'outboundEvents',
      'infinite',
      { pageSize, status: params.status, resourceType: params.resourceType },
    ],
    queryFn: ({ pageParam }) =>
      BookinglayerService.getOutboundEvents({
        limit: pageSize,
        offset: pageParam as number,
        status: params.status,
        resourceType: params.resourceType,
      }),
    initialPageParam: 0,
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((sum, page) => sum + page.events.length, 0);
      // A page shorter than requested means the window is exhausted even if
      // `count` still disagrees (rows can be pruned between pages).
      if (!last.events.length || loaded >= last.count) return undefined;
      return loaded;
    },
    enabled: options?.enabled ?? true,
  });

  return {
    events: q.data?.pages.flatMap((p) => p.events) ?? [],
    total: q.data?.pages[0]?.count ?? 0,
    isLoading: q.isLoading,
    isFetchingNextPage: q.isFetchingNextPage,
    hasNextPage: q.hasNextPage,
    fetchNextPage: q.fetchNextPage,
  };
}

/**
 * Re-enqueue the original push task for a failed outbound event.
 *
 * Uses the source object's CURRENT state (not the snapshot stored on the
 * failed event) — so editing the underlying guest / booking and then
 * retrying pushes the corrected payload. The retry creates a new
 * BookinglayerOutboundEvent; the original row is never modified.
 */
export function useRetryOutboundEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      BookinglayerService.retryOutboundEvent(eventId),
    onSuccess: () => {
      // Invalidate every outbound-events query — pagination + filter
      // permutations all share the prefix.
      qc.invalidateQueries({ queryKey: ['bookinglayer', 'outboundEvents'] });
      qc.invalidateQueries({ queryKey: KEYS.status });
    },
  });
}
